import React, { memo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { validators } from 'investira.sdk';
import { Loading, CenterInView } from 'investira.react.components';
import services from '../../../services';
import app from '../../../utils/app';
import usePageReload from '../../../hooks/usePageReload';

import {
  acUserUpdate,
  acInfoChange,
  acEntidadeChanged,
  acEntidadeCurrentMembrosChanged,
  acIndicadoresChanged,
  acCarteirasChanged,
  acMercadosChanged,
  acReportsEnquadramentoRegrasChanged,
  acOutputsEnumChanged,
  acReportsEnquadramentosChanged
} from '../../../store/actions';

import { ErrorDataView } from '../../../components/molecules';

import withResponseHandling from '../../../hoc/withResponseHandling';

const Preload = memo(props => {
  const dispatch = useDispatch();
  const isReloaded = usePageReload();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState(null);

  function verifyToken() {
    services.info.read(
      {},
      () => {
        preload();
      },
      () => {
        app.logout();
      }
    );
  }

  async function baseRequest(pService, pParams = {}, pIsReloaded = false) {
    return await pService(
      { ...pParams, isReloaded: pIsReloaded },
      rRes => {
        return rRes.data;
      },
      rErr => {
        throw rErr;
      }
    );
  }

  function feedRedux(pResponses) {
    try {
      const xDispatches = [
        pData => dispatch(acUserUpdate(pData)),
        pData => dispatch(acInfoChange(pData)),
        pData => dispatch(acEntidadeChanged(pData)),
        pData => dispatch(acIndicadoresChanged(pData)),
        pData => dispatch(acCarteirasChanged(pData)),
        pData => dispatch(acMercadosChanged(pData)),
        pData => dispatch(acReportsEnquadramentoRegrasChanged(pData)),
        pData => dispatch(acOutputsEnumChanged(pData)),
        pData => dispatch(acReportsEnquadramentosChanged(pData)),
        pData => dispatch(acEntidadeCurrentMembrosChanged(pData))
      ];

      pResponses.forEach((pData, pIndex) => {
        xDispatches[pIndex](pData);
      });

      setDataLoaded(true);
    } catch (error) {
      console.error('Preload => feedRedux()', error.message);
      return error;
    }
  }

  async function preload() {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4ee466c3-bf19-45a6-82ed-4de589ff1a98', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: 'log_' + Date.now() + '_preload_outputs',
        timestamp: Date.now(),
        location: 'src/app/Root/PrivateSession/Preload.jsx:84',
        message: 'Preload outputs service snapshot',
        data: {
          hasServices: !!services,
          hasOutputs: !!(services && services.outputs),
          outputsKeys: services && services.outputs ? Object.keys(services.outputs) : null,
          hasEnumaration:
            services && services.outputs
              ? Object.prototype.hasOwnProperty.call(services.outputs, 'enumaration')
              : null
        },
        runId: 'pre',
        hypothesisId: 'H1'
      })
    }).catch(() => {});
    // #endregion agent log

    const xHasOutputs = !!(services && services.outputs);
    const xHasEnumaration =
      services && services.outputs && typeof services.outputs.enumaration === 'function';

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4ee466c3-bf19-45a6-82ed-4de589ff1a98', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: 'log_' + Date.now() + '_preload_outputs_decision',
        timestamp: Date.now(),
        location: 'src/app/Root/PrivateSession/Preload.jsx:106',
        message: 'Preload outputs enumaration decision',
        data: {
          hasOutputs: xHasOutputs,
          hasEnumaration: xHasEnumaration
        },
        runId: 'fix1',
        hypothesisId: 'H2'
      })
    }).catch(() => {});
    // #endregion agent log

    const xRequests = [
      baseRequest(services.user.read, isReloaded),
      baseRequest(services.info.read),
      baseRequest(services.entidades.usuario.entidades),
      baseRequest(services.indicadores.list),
      baseRequest(services.carteiras.list, {
        favorito: 1
      }),
      baseRequest(services.ativosbase.mercado),
      baseRequest(services.enquadramentosRegras.sys),
      xHasEnumaration ? baseRequest(services.outputs.enumaration) : Promise.resolve(null),
      baseRequest(services.enquadramentos.list),
      baseRequest(services.entidadesUsuario.list)
    ];

    await Promise.all(xRequests)
      .then(rResponses => {
        feedRedux(rResponses);
      })
      .catch(rErr => {
        const xCallbacks = {
          err400: () => setError(true),
          err404: () => setError(true),
          err500: () => setError(true)
        };
        props.responseErrorHandling(rErr, xCallbacks);
      });
  }

  function handleRetry() {
    setError(null);
    verifyToken();
  }

  // Testa o token e inicia o preload
  useEffect(() => {
    props.isLoggedIn && verifyToken();
  }, [props.isLoggedIn]);

  if (!validators.isNull(error)) {
    return <ErrorDataView handleClick={handleRetry} />;
  }

  if (props.isLoggedIn === false) {
    return props.children;
  }

  if (dataLoaded) {
    return props.children;
  } else {
    return (
      <CenterInView>
        <Loading />
      </CenterInView>
    );
  }
});

Preload.propTypes = {
  children: PropTypes.element
};

Preload.defaultProps = {};

Preload.displayName = 'Preload';

export default withResponseHandling(Preload);
